import React, { useState, useMemo, useEffect } from 'react';
import {
  X,
  Search,
  FolderKanban,
  UploadCloud,
  Plus,
  Check,
  Crown,
  UserPlus,
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  Share2,
  Users,
  Eye,
  Pencil,
  Trash2,
  RotateCcw,
  Archive,
  LogOut,
} from 'lucide-react';
import { Project, ProjectStatus, ProjectAccessRole } from '../../types.ts';
import { ApiService } from '../../services/ApiService.ts';
import { useTranslation } from '../../i18n.ts';
import { useTheme } from '../../hooks/useTheme.ts';
import { useToast } from './Toast.tsx';
import { Modal } from '../ui/Modal.tsx';
import { useConfirm } from './ModalDialogs.tsx';
import InviteMemberModal from '../modals/InviteMemberModal.tsx';
import EditProjectModal from '../modals/EditProjectModal.tsx';
import { useOrganizationStore } from '../../stores/useOrganizationStore.ts';
import { useSessionStore } from '../../stores/useSessionStore.ts';

interface PendingInvitation {
  id: string;
  token: string;
  project_id: string;
  role: string;
  created_at: string;
  expires_at: string;
  project_name: string;
  project_description: string;
  inviter_email: string;
}

interface ProjectSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
  projects: Project[];
  currentProject: Project | null;
  onSelectProject: (project: Project) => void;
  onCreateProject: () => void;
  onImportGedcom: () => void;
  onProjectDeleted?: () => void;
  onProjectUpdated?: (project: Project) => void;
  pendingInvitations?: PendingInvitation[];
  onInvitationHandled?: () => void;
  /** #311: open de switcher direct op een specifieke tab. Gebruikt door de
   *  "Naar Prullenbak gaan"-knop in PrivacySettings. */
  initialTab?: 'projects' | 'trash';
}

const ALL_STATUSES: ProjectStatus[] = ['active', 'on_hold', 'delivered', 'archived'];

// #623: per-status kleurcodering — active=goud (lopend), on_hold=blauw (nieuw),
// delivered=emerald (afgerond), archived=forest/sage (opgeleverd)
const STATUS_STYLES: Record<string, { light: string; dark: string }> = {
  active: {
    light: 'bg-pt-gold/10 text-pt-gold-text dark:text-[var(--gold-text)] border-pt-gold/30',
    dark: 'bg-pt-gold/10 text-pt-gold border-pt-gold/30',
  },
  on_hold: {
    light: 'bg-blue-50 text-blue-600 border-blue-200',
    dark: 'bg-blue-500/10 text-blue-300 border-blue-400/30',
  },
  delivered: {
    light: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    dark: 'bg-emerald-500/10 text-emerald-300 border-emerald-400/30',
  },
  archived: {
    light: 'bg-pt-forest/5 text-pt-sage-text border-pt-forest/10',
    dark: 'bg-white/5 text-[var(--text-muted)] border-white/10',
  },
};

const getStatusStyle = (status: ProjectStatus, isDark: boolean) => {
  const styles = STATUS_STYLES[status];
  if (!styles)
    return isDark
      ? 'bg-white/5 text-[var(--text-muted)] border-white/10'
      : 'bg-gray-50 text-gray-500 border-gray-200';
  return isDark ? styles.dark : styles.light;
};

type Tab = 'projects' | 'trash';

const ProjectSwitcher: React.FC<ProjectSwitcherProps> = ({
  isOpen,
  onClose,
  projects,
  currentProject,
  onSelectProject,
  onCreateProject,
  onImportGedcom,
  onProjectDeleted,
  onProjectUpdated,
  pendingInvitations = [],
  onInvitationHandled,
  initialTab,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toast } = useToast();
  const confirmModal = useConfirm();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<ProjectStatus>>(
    new Set(['active', 'on_hold', 'delivered'])
  );
  const [processingInvite, setProcessingInvite] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('projects');

  const [onlineCounts, setOnlineCounts] = useState<Record<string, number>>({});
  const [inviteProjectId, setInviteProjectId] = useState<string | null>(null);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [memberStats, setMemberStats] = useState<
    Record<string, { editors: number; viewers: number; invited: number }>
  >({});

  // Delete state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Trash state
  const [deletedProjects, setDeletedProjects] = useState<Project[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashActioning, setTrashActioning] = useState<string | null>(null);

  // #596 — gearchiveerde projecten worden los geladen (de `projects`-prop sluit ze
  // uit), zodat de "Gearchiveerd"-filterpil ze daadwerkelijk kan tonen.
  const [archivedProjects, setArchivedProjects] = useState<Project[]>([]);

  // Reset search + fetch online counts + member stats when opening.
  // #311: respecteer initialTab zodat "Naar Prullenbak gaan" direct opent
  // op de Prullenbak-tab i.p.v. eerst Projecten te tonen.
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setActiveTab(initialTab || 'projects');
      setDeleteConfirmId(null);
      setDeleteConfirmName('');
      ApiService.getProjectsOnline()
        .then(setOnlineCounts)
        .catch((err) => console.warn('getProjectsOnline failed:', err.message || err));
      // Fetch deleted projects count for trash tab badge
      loadDeletedProjects();
      // #596 — laad gearchiveerde projecten zodat de "Gearchiveerd"-pil telling +
      // projecten toont (en een gearchiveerd project terugvindbaar/her-activeerbaar is).
      loadArchivedProjects();
      // Fetch member counts for all projects
      Promise.all(
        projects.map(async (p) => {
          try {
            const members = await ApiService.getProjectMembers(p.id);
            const editors = members.filter((m: any) => m.role === 'editor' && m.accepted_at).length;
            const viewers = members.filter((m: any) => m.role === 'viewer' && m.accepted_at).length;
            const invited = members.filter((m: any) => !m.accepted_at && m.role !== 'owner').length;
            return [p.id, { editors, viewers, invited }] as const;
          } catch {
            return [p.id, { editors: 0, viewers: 0, invited: 0 }] as const;
          }
        })
      ).then((results) => {
        const stats: Record<string, { editors: number; viewers: number; invited: number }> = {};
        for (const [id, s] of results) stats[id] = s;
        setMemberStats(stats);
      });
    }
  }, [isOpen, projects, initialTab]);

  // Load trash when switching to trash tab
  useEffect(() => {
    if (activeTab === 'trash' && isOpen) {
      loadDeletedProjects();
    }
  }, [activeTab, isOpen]);

  const loadDeletedProjects = async () => {
    setTrashLoading(true);
    try {
      const all = await ApiService.getProjectsFlat(true);
      setDeletedProjects(all.filter((p) => p.deleted_at));
    } catch (e) {
      console.error(e);
    } finally {
      setTrashLoading(false);
    }
  };

  const loadArchivedProjects = async () => {
    try {
      const all = await ApiService.getProjectsFlat({ include_archived: true });
      setArchivedProjects(all.filter((p) => p.status === 'archived' && !p.deleted_at));
    } catch (e) {
      console.warn('loadArchivedProjects failed:', e);
    }
  };

  // #596 — combineer de actieve lijst met de los-geladen archived projecten zodat de
  // telling én de "Gearchiveerd"-filter ze kunnen tonen (geen dubbele entries).
  const allProjects = useMemo(() => {
    if (archivedProjects.length === 0) return projects;
    const seen = new Set(projects.map((p) => p.id));
    return [...projects, ...archivedProjects.filter((p) => !seen.has(p.id))];
  }, [projects, archivedProjects]);

  const filteredProjects = useMemo(() => {
    return allProjects.filter((p) => {
      if (!activeFilters.has(p.status)) return false;
      if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    });
  }, [allProjects, activeFilters, searchTerm]);

  // Plan 3 (#778) — groepeer in "Mijn projecten" + "Organisatieprojecten".
  // Fallback: als de user geen org heeft, render één lijst zonder header
  // (gedrag identiek aan vóór plan 3).
  const orgFromStore = useOrganizationStore((s) => s.currentOrg);
  const orgFromSession = useSessionStore((s) => s.user?.organization || null);
  const activeOrg = orgFromStore || orgFromSession;
  const { personalProjects, organizationProjects } = useMemo(() => {
    const personal: Project[] = [];
    const org: Project[] = [];
    for (const p of filteredProjects) {
      if (p.organization_id) org.push(p);
      else personal.push(p);
    }
    return { personalProjects: personal, organizationProjects: org };
  }, [filteredProjects]);
  const showGroupedListing = !!activeOrg && organizationProjects.length > 0;

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { active: 0, on_hold: 0, delivered: 0, archived: 0 };
    for (const p of allProjects) {
      if (counts[p.status] !== undefined) counts[p.status]++;
    }
    return counts;
  }, [allProjects]);

  const allActive = ALL_STATUSES.every((s) => activeFilters.has(s));

  const toggleFilter = (status: ProjectStatus) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        if (next.size > 1) next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allActive) {
      setActiveFilters(new Set(['active', 'on_hold', 'delivered']));
    } else {
      setActiveFilters(new Set(ALL_STATUSES));
    }
  };

  const getStatusLabel = (status: ProjectStatus) => {
    switch (status) {
      case 'active':
        return t('projectStatusActive');
      case 'on_hold':
        return t('projectStatusOnHold');
      case 'delivered':
        return t('projectStatusDelivered');
      case 'archived':
        return t('projectStatusArchived');
      case 'draft':
        return t('projectStatusDraft');
      case 'in_review':
        return t('projectStatusInReview');
      default:
        return status;
    }
  };

  const getRoleLabel = (role: ProjectAccessRole) => {
    switch (role) {
      case 'owner':
        return t('owner');
      case 'editor':
        return t('editor');
      case 'viewer':
        return t('viewer');
      default:
        return role;
    }
  };

  const handleAcceptInvitation = async (inv: PendingInvitation) => {
    setProcessingInvite(inv.id);
    try {
      await ApiService.acceptInvitation(inv.token);
      onInvitationHandled?.();
      try {
        const freshProjects = await ApiService.getProjectsFlat();
        const joinedProject = freshProjects.find((p: Project) => p.id === inv.project_id);
        if (joinedProject) onSelectProject(joinedProject);
      } catch {
        /* best effort */
      }
      toast(t('invitationAccepted'), 'success');
    } catch (err) {
      console.error('Failed to accept invitation:', err);
      toast(t('invitationFailed'), 'error');
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleDeclineInvitation = async (inv: PendingInvitation) => {
    setProcessingInvite(inv.id);
    try {
      await ApiService.declineInvitation(inv.token);
      onInvitationHandled?.();
      toast(t('invitationDeclined'), 'success');
    } catch (err) {
      console.error('Failed to decline invitation:', err);
      toast(t('invitationFailed'), 'error');
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleDeleteProject = async (project: Project) => {
    if (deleteConfirmName !== project.name) return;
    setDeleting(true);
    try {
      await ApiService.softDeleteProject(project.id);
      toast(t('projectDeleted'), 'success');
      setDeleteConfirmId(null);
      setDeleteConfirmName('');
      onProjectDeleted?.();
    } catch (err: any) {
      toast(err.message || t('uiProjectDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (id: string) => {
    setTrashActioning(id);
    try {
      await ApiService.restoreProject(id);
      toast(t('projectRestored'), 'success');
      await loadDeletedProjects();
      onProjectDeleted?.(); // Triggers project list refresh in parent
    } catch (e) {
      toast(t('restoreFailed'), 'error');
    } finally {
      setTrashActioning(null);
    }
  };

  const handleHardPurge = async (id: string) => {
    const confirmed = await confirmModal({
      title: t('delete'),
      message: t('confirmHardPurge'),
      destructive: true,
    });
    if (!confirmed) return;
    setTrashActioning(id);
    try {
      await ApiService.hardPurgeProject(id);
      toast(t('projectPurged'), 'success');
      await loadDeletedProjects();
    } catch (e) {
      toast(t('deleteFailed'), 'error');
    } finally {
      setTrashActioning(null);
    }
  };

  const handleProjectUpdated = (updated: Project) => {
    onProjectUpdated?.(updated);
    setEditProject(null);
  };

  /**
   * Plan 3 (#778) — render een enkele project-card. Geëxtraheerd zodat
   * de organisatie-/persoonlijke groepen dezelfde card-rendering delen
   * zonder duplicatie.
   */
  const renderProjectCard = (project: Project) => {
    const isActive = currentProject?.id === project.id;
    const isOwner = project.memberRole === 'owner';
    const isDeleting = deleteConfirmId === project.id;
    return (
      <div
        key={project.id}
        className={`text-left p-5 rounded-[28px] border transition-[transform,box-shadow,border-color] hover:shadow-lg hover:-translate-y-0.5 group ${
          isActive
            ? 'bg-white dark:bg-[var(--bg-surface)] border-pt-gold/40 ring-2 ring-pt-gold/20 shadow-md'
            : 'bg-white dark:bg-[var(--bg-surface)] border-pt-forest/5 dark:border-white/10 shadow-sm hover:border-pt-forest/10 dark:hover:border-white/20'
        }`}
      >
        <button onClick={() => onSelectProject(project)} className="w-full text-left">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <span
                className={`text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${getStatusStyle(project.status, isDark)}`}
              >
                {getStatusLabel(project.status)}
              </span>
              {(() => {
                const stats = memberStats[project.id];
                const shared = stats ? stats.editors + stats.viewers + stats.invited > 0 : false;
                if (!shared) return null;
                const label = t('sharedProject');
                return (
                  <span
                    className="flex items-center text-pt-gold-text dark:text-[var(--gold-text)]"
                    title={label}
                    aria-label={label}
                  >
                    <Users size={12} />
                  </span>
                );
              })()}
              {(onlineCounts[project.id] ?? 0) > 0 && (
                <span className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-emerald-600">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  {onlineCounts[project.id]}
                </span>
              )}
            </div>
            {isActive && (
              <Check
                size={16}
                className="text-pt-gold-text dark:text-[var(--gold-text)] shrink-0"
              />
            )}
          </div>
          <h3
            className={`font-bold text-sm leading-tight mb-2 truncate ${
              isActive
                ? 'text-pt-gold-text dark:text-[var(--gold-text)]'
                : 'text-pt-forest dark:text-[var(--text-main)] group-hover:text-pt-gold-text group-hover:dark:text-[var(--gold-text)]'
            } transition-colors`}
          >
            {project.name}
          </h3>
          <div className="flex items-center gap-2 text-[9px] text-pt-sage-text dark:text-[var(--text-muted)] font-bold uppercase tracking-widest">
            {isOwner && (
              <Crown size={10} className="text-pt-gold-text dark:text-[var(--gold-text)]" />
            )}
            <span>{getRoleLabel(project.memberRole ?? 'viewer')}</span>
            {project.created_at && (
              <>
                <span className="w-1 h-1 rounded-full bg-pt-forest/10 dark:bg-white/10" />
                <span>{new Date(project.created_at).toLocaleDateString()}</span>
              </>
            )}
          </div>
        </button>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-pt-forest/5 dark:border-white/5">
          <div className="flex items-center gap-3 text-[9px] text-pt-sage-text dark:text-[var(--text-muted)]">
            {(memberStats[project.id]?.editors ?? 0) > 0 && (
              <span className="flex items-center gap-1" title={t('editor')}>
                <Pencil size={9} /> {memberStats[project.id].editors}
              </span>
            )}
            {(memberStats[project.id]?.viewers ?? 0) > 0 && (
              <span className="flex items-center gap-1" title={t('viewer')}>
                <Eye size={9} /> {memberStats[project.id].viewers}
              </span>
            )}
            {(memberStats[project.id]?.invited ?? 0) > 0 && (
              <span
                className="flex items-center gap-1 text-pt-gold-text dark:text-[var(--gold-text)]"
                title={t('invited')}
              >
                <Mail size={9} /> {memberStats[project.id].invited}
              </span>
            )}
            {!memberStats[project.id]?.editors &&
              !memberStats[project.id]?.viewers &&
              !memberStats[project.id]?.invited && (
                <span className="flex items-center gap-1 opacity-50">
                  <Users size={9} /> {t('notShared')}
                </span>
              )}
          </div>
          <div className="flex items-center gap-1.5">
            {isOwner && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditProject(project);
                  }}
                  /* #838 — idle-state was bg/5 + sage-text (te laag contrast,
                     tester ervaart als 'hover-revealed'). Bumped naar /10 +
                     forest-text voor leesbare idle, hover behoudt accent. */
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-pt-forest/10 dark:bg-white/10 text-pt-forest dark:text-[var(--text-main)] hover:bg-pt-forest/20 dark:hover:bg-white/20 hover:text-pt-gold-text hover:dark:text-[var(--gold-text)] transition-colors text-[9px] font-bold uppercase tracking-wider"
                >
                  <Pencil size={11} />
                  {t('edit')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setInviteProjectId(project.id);
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-pt-gold/10 dark:bg-pt-gold/10 text-pt-gold-text dark:text-[var(--gold-text)] hover:bg-pt-gold/20 dark:hover:bg-pt-gold/20 transition-colors text-[9px] font-bold uppercase tracking-wider"
                >
                  <Share2 size={11} />
                  {t('share')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmId(project.id);
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-pt-ruby/5 dark:bg-white/5 text-pt-ruby dark:text-[var(--text-muted)] hover:bg-pt-ruby/10 dark:hover:bg-pt-ruby-light/10 hover:text-pt-ruby-hover dark:hover:text-pt-ruby-light transition-colors text-[9px] font-bold uppercase tracking-wider"
                >
                  <Trash2 size={11} />
                </button>
              </>
            )}
            {!isOwner && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setInviteProjectId(project.id);
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-pt-ruby/5 dark:bg-white/5 text-pt-ruby dark:text-pt-ruby-light hover:bg-pt-ruby/10 dark:hover:bg-pt-ruby-light/10 hover:text-pt-ruby-hover dark:hover:text-pt-ruby-light transition-colors text-[9px] font-bold uppercase tracking-wider"
                title={t('leaveProject')}
              >
                <LogOut size={11} />
                {t('leaveProject')}
              </button>
            )}
          </div>
        </div>
        {isDeleting && (
          <div className="mt-3 pt-3 border-t border-pt-ruby/20 dark:border-pt-ruby-light/30">
            <p className="text-[10px] text-pt-ruby dark:text-pt-ruby-light font-bold mb-2">
              {t('deleteProjectConfirmPrompt', { name: project.name })}
            </p>
            <input
              type="text"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder={project.name}
              className="w-full px-3 py-2 bg-pt-ruby/5 dark:bg-pt-ruby-light/5 border border-pt-ruby/20 dark:border-pt-ruby-light/30 rounded-xl text-sm text-pt-forest dark:text-[var(--text-main)] focus:ring-2 focus:ring-pt-ruby/30 outline-none mb-2"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteProject(project);
                }}
                disabled={deleteConfirmName !== project.name || deleting}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-pt-ruby text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-pt-ruby-hover transition-colors disabled:opacity-50"
              >
                {deleting && <Loader2 size={10} className="animate-spin" />}
                {t('confirmDeleteProjectAction')}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirmId(null);
                  setDeleteConfirmName('');
                }}
                className="px-3 py-2 bg-white dark:bg-white/5 text-pt-sage-text border border-pt-forest/10 dark:border-white/10 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-pt-forest/5 dark:hover:bg-white/10 transition-colors"
              >
                {t('cancel')}
              </button>
            </div>
            <p className="text-[8px] text-pt-sage-text dark:text-[var(--text-muted)] mt-2">
              {t('deleteProjectRecoveryHint')}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        titleId="modal-title-project-switcher"
        size="xl"
        className="flex flex-col"
      >
        <div className="flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-6 md:p-8 border-b border-pt-forest/10 dark:border-white/10 bg-white dark:bg-[var(--bg-surface)] shrink-0">
            <div className="flex justify-between items-center">
              <div>
                <h2
                  id="modal-title-project-switcher"
                  className="text-2xl font-serif font-bold text-pt-forest dark:text-[var(--text-main)]"
                >
                  {t('allProjects')}
                </h2>
                <p className="text-sm text-pt-sage-text dark:text-[var(--text-muted)] font-medium mt-1">
                  {projects.length} {t('projectCount')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onCreateProject}
                  data-tour="new-project-btn"
                  className="px-4 py-2.5 bg-pt-forest text-pt-gold dark:bg-pt-forest dark:text-pt-gold rounded-xl font-black uppercase tracking-widest text-[9px] hover:bg-pt-forest/90 transition-colors flex items-center gap-2"
                >
                  <FolderKanban size={14} /> {t('newProject')}
                </button>
                <button
                  onClick={onImportGedcom}
                  className="px-4 py-2.5 bg-pt-gold/10 text-pt-gold-text dark:text-[var(--gold-text)] rounded-xl font-black uppercase tracking-widest text-[9px] hover:bg-pt-gold/20 transition-colors flex items-center gap-2"
                >
                  <UploadCloud size={14} /> {t('importGedcom')}
                </button>
                <button
                  onClick={onClose}
                  className="p-3 hover:bg-pt-forest/5 dark:hover:bg-white/5 rounded-2xl transition-colors"
                  aria-label={t('close')}
                >
                  <X size={24} className="text-pt-sage-text dark:text-[var(--text-muted)]" />
                </button>
              </div>
            </div>

            {/* Tab switcher: Projects | Trash */}
            <div className="flex gap-1 mt-5 mb-4 bg-pt-cream dark:bg-white/5 rounded-2xl p-1">
              <button
                onClick={() => setActiveTab('projects')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${
                  activeTab === 'projects'
                    ? 'bg-white dark:bg-[var(--bg-surface)] text-pt-forest dark:text-[var(--text-main)] shadow-sm'
                    : 'text-pt-sage-text dark:text-[var(--text-muted)] hover:text-pt-forest dark:hover:text-[var(--text-main)]'
                }`}
              >
                <FolderKanban size={12} />
                {t('projects')}
              </button>
              <button
                onClick={() => setActiveTab('trash')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${
                  activeTab === 'trash'
                    ? 'bg-white dark:bg-[var(--bg-surface)] text-pt-forest dark:text-[var(--text-main)] shadow-sm'
                    : 'text-pt-sage-text dark:text-[var(--text-muted)] hover:text-pt-forest dark:hover:text-[var(--text-main)]'
                }`}
              >
                <Archive size={12} />
                {t('trash')}
                {deletedProjects.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-pt-ruby/10 dark:bg-white/10 text-pt-ruby dark:text-[var(--text-muted)] rounded-full text-[8px]">
                    {deletedProjects.length}
                  </span>
                )}
              </button>
            </div>

            {/* Search + Filters (only for projects tab) */}
            {activeTab === 'projects' && (
              <div className="space-y-3">
                <div className="relative">
                  <Search
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-pt-sage-text dark:text-[var(--text-muted)]"
                    size={16}
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('quickSearch') + '...'}
                    className="w-full pl-11 pr-4 py-3 bg-pt-cream dark:bg-[var(--bg-surface)] border border-pt-forest/10 dark:border-white/10 rounded-2xl text-sm font-bold text-pt-forest dark:text-[var(--text-main)] placeholder:text-pt-sage-text/50 dark:placeholder:text-[var(--text-muted)] focus:ring-4 focus:ring-pt-gold/20 outline-none transition-colors"
                    autoFocus
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={toggleAll}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors border ${
                      allActive
                        ? 'bg-pt-forest text-pt-gold border-pt-forest'
                        : 'bg-white dark:bg-white/5 text-pt-sage-text dark:text-[var(--text-muted)] border-pt-forest/10 dark:border-white/10 hover:bg-pt-forest/5 dark:hover:bg-white/10'
                    }`}
                  >
                    {t('allStatuses')}
                  </button>
                  {ALL_STATUSES.map((status) => (
                    <button
                      key={status}
                      onClick={() => toggleFilter(status)}
                      className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors border ${
                        activeFilters.has(status)
                          ? getStatusStyle(status, isDark)
                          : 'bg-white dark:bg-white/5 text-pt-sage-text/40 dark:text-[var(--text-muted)] border-pt-forest/5 dark:border-white/10 hover:bg-pt-forest/5 dark:hover:bg-white/10'
                      }`}
                    >
                      {getStatusLabel(status)} ({statusCounts[status] || 0})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto p-6 md:p-8">
            {activeTab === 'projects' ? (
              <>
                {/* Pending Invitations */}
                {pendingInvitations.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-[10px] font-black text-pt-gold-text dark:text-[var(--gold-text)] uppercase tracking-widest mb-3 flex items-center gap-2 dark:text-pt-gold">
                      <Mail size={12} />
                      {t('pendingInvitations')} ({pendingInvitations.length})
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {pendingInvitations.map((inv) => {
                        const isProcessing = processingInvite === inv.id;
                        return (
                          <div
                            key={inv.id}
                            className="text-left p-5 rounded-[28px] border border-pt-gold/30 bg-pt-gold/5 dark:bg-pt-gold/10 shadow-sm"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <span className="text-[8px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border bg-pt-gold/15 text-pt-gold-text dark:text-[var(--gold-text)] border-pt-gold/30">
                                {t('invited')}
                              </span>
                              <UserPlus
                                size={14}
                                className="text-pt-gold-text dark:text-[var(--gold-text)] shrink-0"
                              />
                            </div>

                            <h3 className="font-bold text-sm leading-tight mb-1 truncate text-pt-forest dark:text-[var(--text-main)]">
                              {inv.project_name}
                            </h3>

                            <div className="flex items-center gap-2 text-[9px] text-pt-sage-text dark:text-[var(--text-muted)] font-bold uppercase tracking-widest mb-3">
                              <span>{t(inv.role as 'editor' | 'viewer')}</span>
                              <span className="w-1 h-1 rounded-full bg-pt-forest/10 dark:bg-white/10" />
                              <span className="normal-case tracking-normal">
                                {inv.inviter_email}
                              </span>
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAcceptInvitation(inv)}
                                disabled={isProcessing}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-pt-forest text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-pt-forest/90 transition-colors disabled:opacity-50"
                              >
                                {isProcessing ? (
                                  <Loader2 size={10} className="animate-spin" />
                                ) : (
                                  <CheckCircle size={10} />
                                )}
                                {t('accept')}
                              </button>
                              <button
                                onClick={() => handleDeclineInvitation(inv)}
                                disabled={isProcessing}
                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white dark:bg-white/5 text-pt-sage-text dark:text-[var(--text-muted)] border border-pt-forest/10 dark:border-white/10 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-pt-forest/5 dark:hover:bg-white/10 transition-colors disabled:opacity-50"
                              >
                                {isProcessing ? (
                                  <Loader2 size={10} className="animate-spin" />
                                ) : (
                                  <XCircle size={10} />
                                )}
                                {t('decline')}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {filteredProjects.length === 0 && pendingInvitations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <FolderKanban size={48} className="text-pt-sage-text/20 mb-4" />
                    <p className="text-pt-sage-text dark:text-[var(--text-muted)] font-bold">
                      {t('noProjectsFound')}
                    </p>
                  </div>
                ) : (
                  filteredProjects.length > 0 && (
                    <div className="space-y-8">
                      {/* Plan 3 (#778) — gegroepeerde weergave: organisatie-sectie
                          eerst (Model A werkgever per spec), dan persoonlijke
                          projecten. Wanneer de user geen org heeft of er geen
                          org-projecten zijn, valt het terug op één lijst zonder
                          headers (legacy gedrag). */}
                      {showGroupedListing ? (
                        <>
                          {organizationProjects.length > 0 && (
                            <section>
                              <h3 className="text-[10px] font-black text-pt-forest dark:text-[var(--text-main)] uppercase tracking-widest mb-3 flex items-center gap-2">
                                <Users
                                  size={12}
                                  className="text-pt-gold-text dark:text-[var(--gold-text)]"
                                />
                                {activeOrg?.name} — {t('organizationProjects')} (
                                {organizationProjects.length})
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {organizationProjects.map(renderProjectCard)}
                              </div>
                            </section>
                          )}
                          <div className="border-t border-pt-forest/10 dark:border-white/10" />
                          <section>
                            <h3 className="text-[10px] font-black text-pt-forest dark:text-[var(--text-main)] uppercase tracking-widest mb-3 flex items-center gap-2">
                              <FolderKanban size={12} className="text-pt-sage-text" />
                              {t('myProjects')} ({personalProjects.length})
                            </h3>
                            {personalProjects.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {personalProjects.map(renderProjectCard)}
                              </div>
                            ) : (
                              <p className="text-xs text-pt-sage-text italic">
                                {t('noPersonalProjects')}
                              </p>
                            )}
                          </section>
                        </>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {filteredProjects.map(renderProjectCard)}
                        </div>
                      )}
                    </div>
                  )
                )}
              </>
            ) : (
              /* Trash tab */
              <div>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm font-bold text-pt-forest dark:text-[var(--text-main)] flex items-center gap-2">
                    <Archive size={16} />
                    {t('privacyDeletedProjects')}
                  </p>
                  <span className="text-[10px] text-pt-sage-text dark:text-[var(--text-muted)] font-medium">
                    {t('privacyAutoDeleteHint')}
                  </span>
                </div>

                {trashLoading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 size={24} className="animate-spin text-pt-gold" />
                  </div>
                ) : deletedProjects.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <CheckCircle size={48} className="text-emerald-400/40 mb-4" />
                    <p className="text-pt-sage-text dark:text-[var(--text-muted)] font-bold">
                      {t('privacyTrashEmpty')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {deletedProjects.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between p-4 rounded-2xl border border-pt-forest/5 dark:border-white/10 bg-white dark:bg-[var(--bg-surface)]"
                      >
                        <div>
                          <p className="font-bold text-sm text-pt-forest dark:text-[var(--text-main)]">
                            {p.name}
                          </p>
                          <p className="text-[10px] text-pt-sage-text dark:text-[var(--text-muted)] mt-0.5">
                            {t('privacyDeletedOn')}: {new Date(p.deleted_at!).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-4">
                          <button
                            disabled={!!trashActioning}
                            onClick={() => handleRestore(p.id)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30 rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors disabled:opacity-50"
                          >
                            {trashActioning === p.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <RotateCcw size={12} />
                            )}
                            {t('privacyRestore')}
                          </button>
                          <button
                            disabled={!!trashActioning}
                            onClick={() => handleHardPurge(p.id)}
                            className="flex items-center gap-1.5 px-3 py-2 bg-transparent dark:bg-transparent text-pt-ruby dark:text-pt-ruby-light border border-pt-ruby/30 dark:border-pt-ruby-light/30 rounded-xl text-[9px] font-black uppercase tracking-widest hover:border-pt-ruby/60 dark:hover:border-pt-ruby-light/60 transition-colors disabled:opacity-50"
                          >
                            {trashActioning === p.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <Trash2 size={12} />
                            )}
                            {t('privacyPurge')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {inviteProjectId && (
        <InviteMemberModal
          isOpen={!!inviteProjectId}
          onClose={() => setInviteProjectId(null)}
          projectId={inviteProjectId}
          currentUserRole={projects.find((p) => p.id === inviteProjectId)?.memberRole ?? null}
          onLeft={() => {
            setInviteProjectId(null);
            onProjectDeleted?.();
          }}
        />
      )}

      {editProject && (
        <EditProjectModal
          isOpen={!!editProject}
          onClose={() => setEditProject(null)}
          project={editProject}
          onUpdated={handleProjectUpdated}
        />
      )}
    </>
  );
};

export default React.memo(ProjectSwitcher);
